import React, { RefObject, useRef, useMemo, useState, useLayoutEffect, useCallback } from 'react';
import './DateFilter.scss';
import { ALL_DATES_PILL_LABEL, splitDatesByVisibleCount } from './dateFilterOverflow';
import { useDateFilterOverflow } from './useDateFilterOverflow';

interface DateOptionProps {
  isActive: boolean,
  label: string,
  className?: string,
  onSelectedDate: () => void ,
  selectedCount?: number,
  useFixedPillLayout?: boolean,
}

export interface DatesProps {
  showAllDates: boolean;
  selectedDate: string;
  dates: string[];
  /** Required by parent callers; reserved for future locale-aware formatting in this component. */
  timeZone: string;
  onSelectedDateChange: (date: string) => void;
  isTogglePastMeetingsBtnShown: boolean;
  changeShowAllDates?: () => void;
  withoutAllDays?: boolean;
  selectedCountPerDay?: {[ date: string ]: number };
  showTracksSection?: boolean;
  maxVisibleDatePills?: number;
}

const DateOption = ({
  isActive,
  label,
  onSelectedDate,
  className = '',
  selectedCount,
  useFixedPillLayout = false,
}: DateOptionProps): JSX.Element | null => {
  const buttonStyle = useFixedPillLayout
    ? { cursor: 'pointer' as const }
    : {
      color: 'var(--primary-color)',
      fontWeight: 700,
      lineHeight: 1.3,
      borderRadius: '7px',
      cursor: 'pointer',
      display: 'flex',
      maxWidth: '120px',
      width: 'max-content',
      whiteSpace: 'pre' as const,
    };

  const isAllDatesPill = useFixedPillLayout && label === ALL_DATES_PILL_LABEL;

  const buttonClassName = useFixedPillLayout
    ? `date-filter__option outline-auto-visible ${className} ${isActive ? 'date-filter__option--active' : ''}${isAllDatesPill ? ' date-filter__option--all-dates' : ''}`
    : `date-filter__option outline-auto-visible font-size-11px p-2 ${className} flex flex-col items-start ${isActive ? 'date-filter__option--active' : ''}`;

  return (
    <div className='date-filter__pill' data-date-pill-label={label}>
      <button
        type='button'
        style={buttonStyle}
        onClick={onSelectedDate}
        className={buttonClassName}
      >
        <div className='DateOption__label'>{label}</div>
        { selectedCount !== undefined && <div className='DateOption__count font-normal'>{`${selectedCount} Selected`}</div> }
      </button>
    </div>
  );
};

const MoreDates = ({
  list,
  open,
  setOpen,
  selectedDate,
  showAllDates,
  onSelectedDateChange,
  selectedCountPerDay,
  useFixedPillLayout = false,
}: {
  open: boolean,
  showAllDates: boolean,
  selectedDate: string,
  list: (string | null)[],
  setOpen: (i: boolean) => void,
  onSelectedDateChange: (date: string) => void,
  selectedCountPerDay?: {[ date: string ]: number },
  useFixedPillLayout?: boolean,
}): JSX.Element => {
  const optionsButtonRef: RefObject<HTMLDivElement> | null = useRef<HTMLDivElement>(null);

  const handleOutsideClick = useCallback((e: Event) => {
    const target = e.target as HTMLElement;
    if (optionsButtonRef?.current?.contains(target)) {
      return;
    }
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsButtonRef]);

  useLayoutEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const isHiddenDateSelected = !showAllDates && list.includes(selectedDate);
  const isMoreDatesActive = open || isHiddenDateSelected;

  return (
    <div className='date-filter__more-wrapper'>
      <button
        type='button'
        style={useFixedPillLayout
          ? { cursor: 'pointer' }
          : {
            color: 'var(--primary-color)',
            fontWeight: 700,
            lineHeight: 1.3,
            display: 'flex',
            alignItems: 'center',
            borderRadius: '7px',
            cursor: 'pointer',
          }}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup='listbox'
        className={useFixedPillLayout
          ? `date-filter__option date-filter__more outline-auto-visible ${isMoreDatesActive ? 'date-filter__option--active' : ''}`
          : `date-filter__option date-filter__more outline-auto-visible font-size-11px p-2 flex items-center ${isMoreDatesActive ? 'bg-gray-300 date-filter__option--active' : 'bg-gray-100 font-bold' } hover:bg-gray-200`}
      >
        More Dates…
      </button>
      <div
        style={{
          top: '100%',
          zIndex: 1000,
          right: '0.75rem',
          borderRadius: '7px',
          overflowY: 'scroll',
          width: 'max-content',
          maxHeight: '282px',
          position: 'absolute',
          display: open ? 'block' : 'none',
          boxShadow: '0 0 6px 0 rgba(0, 0, 0, 0.16)'
        }}
        ref={optionsButtonRef}
        className='p-4 flex items-center bg-white see-more-box-select-box--container'
        role='listbox'
      >
        {
          list.map((date) => {
            const isActive = date === selectedDate && !showAllDates;
            return (
              <DateOption
                label={date || ''}
                isActive={isActive}
                className='mb-2'
                key={date || 'empty-date'}
                onSelectedDate={() => {
                  setOpen(!open);
                  onSelectedDateChange(date || '');
                }}
                selectedCount={selectedCountPerDay?.[date || '']}
                useFixedPillLayout={useFixedPillLayout}
              />
            );
          })
        }
      </div>
    </div>
  );
};

const DateFilter = (props: DatesProps): JSX.Element => {
  const {
    showAllDates,
    isTogglePastMeetingsBtnShown,
    selectedDate,
    dates,
    onSelectedDateChange,
    changeShowAllDates,
    withoutAllDays,
    selectedCountPerDay,
    showTracksSection,
    maxVisibleDatePills,
  } = props;

  const usesFixedPillCount = maxVisibleDatePills !== undefined;
  const [open, setOpen] = useState(false);
  const trackSectionWidth = showTracksSection ? 64 : 0;

  const fixedPillSplit = useMemo(() => {
    if (!usesFixedPillCount) {
      return { visibleDates: dates, hiddenDates: [] as string[] };
    }
    return splitDatesByVisibleCount(dates, maxVisibleDatePills as number);
  }, [dates, maxVisibleDatePills, usesFixedPillCount]);

  const {
    dateFilterRef,
    headerFilterContent,
    moreDatesMeasureRef,
    measuredHiddenDateLabels,
  } = useDateFilterOverflow({
    enabled: !usesFixedPillCount,
    dates,
    withoutAllDays,
    isTogglePastMeetingsBtnShown,
    trackSectionWidth,
    selectedDate,
    showAllDates,
  });

  const hiddenDateLabels = usesFixedPillCount ? fixedPillSplit.hiddenDates : measuredHiddenDateLabels;
  const visibleDateLabels = usesFixedPillCount ? fixedPillSplit.visibleDates : dates;
  const isShowMoreDate = hiddenDateLabels.length > 0;

  return (
    <div
      ref={dateFilterRef}
      className={`flex items-center date-filter ${usesFixedPillCount ? 'date-filter--fixed-pill-count' : 'flex-grow'}`}
      style={usesFixedPillCount
        ? undefined
        : {
          position: 'relative',
          minWidth: (!isTogglePastMeetingsBtnShown && !trackSectionWidth)
            ? '100%'
            : '200px',
          width: `calc(100% - ${200 + trackSectionWidth}px)`,
        }}
    >
      <div
        className='flex date-filter__container'
        ref={headerFilterContent}
      >
        { !withoutAllDays && changeShowAllDates &&
          <DateOption
            label='All Dates'
            isActive={showAllDates}
            onSelectedDate={changeShowAllDates}
            useFixedPillLayout={usesFixedPillCount}
          />
        }
        {visibleDateLabels
          .map((label: string) => {
            const isActive = label === selectedDate && !showAllDates;
            return (
              <DateOption
                label={label}
                isActive={isActive}
                key={label}
                onSelectedDate={() => onSelectedDateChange(label)}
                selectedCount={selectedCountPerDay?.[label]}
                useFixedPillLayout={usesFixedPillCount}
              />
            );
          })
        }
      </div>
      { isShowMoreDate
        ? (
          <MoreDates
            open={open}
            setOpen={setOpen}
            list={hiddenDateLabels}
            selectedDate={selectedDate}
            showAllDates={showAllDates}
            onSelectedDateChange={onSelectedDateChange}
            selectedCountPerDay={selectedCountPerDay}
            useFixedPillLayout={usesFixedPillCount}
          />
        )
        : null
      }
      { !usesFixedPillCount && (
        <button
          type='button'
          ref={moreDatesMeasureRef}
          aria-hidden
          tabIndex={-1}
          className='date-filter__option date-filter__more outline-auto-visible font-size-11px p-2 flex items-center font-bold'
          style={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        >
          More Dates…
        </button>
      )}
    </div>
  );
};

export {
  DateOption,
  DateFilter,
  MoreDates,
};
